import { useState, useMemo, useCallback, useEffect } from 'react'
import { Box, Typography, Autocomplete, TextField, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ChipsList } from '../../../../../components/chipsList'
import { useContextState } from '../../../../../hooks/theme'
import { getInternalUsers } from '../methods'
import { InternalUser, Step4NotificationsProps } from '../types'
import { useStyles } from '../styles'
import { isEmail } from '../../../../../utils/validate/validate'

function Step4Notifications({ data, onChange, errors }: Step4NotificationsProps) {
  const { t } = useTranslation()
  const styles = useStyles()
  const { colors } = useContextState()
  const [emailsRawInput, setEmailsRawInput] = useState(data.externalEmails.join(', '))
  const [internalUserOptions, setInternalUserOptions] = useState<InternalUser[]>([])
  const [isLoadingInternalUsers, setIsLoadingInternalUsers] = useState(false)

  useEffect(() => {
    setIsLoadingInternalUsers(true)
    getInternalUsers()
      .then(setInternalUserOptions)
      .finally(() => setIsLoadingInternalUsers(false))
  }, [])

  const selectedInternalUsers = useMemo(() => {
    return internalUserOptions.filter((user) => data.selectedInternalUsers.includes(user.id))
  }, [internalUserOptions, data.selectedInternalUsers])

  const handleChangeInternalUsers = useCallback((_: any, value: InternalUser[]) => {
    onChange({ selectedInternalUsers: value.map((user) => user.id) })
  }, [onChange])

  const handleRemoveInternalUser = useCallback((option: { value: string; label: string }) => {
    const newSelected = data.selectedInternalUsers.filter((id) => id !== Number(option.value))
    onChange({ selectedInternalUsers: newSelected })
  }, [data.selectedInternalUsers, onChange])

  const internalUsersAsItems = useMemo(() => {
    return selectedInternalUsers.map((user) => ({
      value: String(user.id),
      label: user.fullName,
    }))
  }, [selectedInternalUsers])

  const internalUserColors = useMemo(() => {
    return Object.fromEntries(
      selectedInternalUsers.map((user) => [String(user.id), colors.color8])
    )
  }, [selectedInternalUsers, colors.color8])

  const handleEmailsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setEmailsRawInput(raw)
    const emails = raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0)
    onChange({ externalEmails: emails })
  }, [onChange])

  const emailError = useMemo(() => {
    const tokens = emailsRawInput
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
    if (tokens.length === 0) return ''
    return tokens.some((e) => !isEmail(e)) ? t('actionPlan.validation.invalidEmails') : ''
  }, [emailsRawInput, t])

  return (
    <Box sx={styles.notificationsContainer}>
      <Typography sx={styles.notificationsTitle}>
        {t('actionPlan.whoReceivesNotifications')}
      </Typography>
      <Typography sx={styles.notificationsSubtitle}>
        {t('actionPlan.configureNotifications')}
      </Typography>

      <Box sx={styles.notificationsSection}>
        <Typography sx={styles.notificationsSectionTitle}>
          {t('general.internalUsers')}
        </Typography>
        <Typography sx={styles.notificationsSectionHelper}>
          {t('actionPlan.selectWhoReceives')}
        </Typography>
        <Box sx={styles.internalUsersSelectContainer}>
          <Autocomplete
            multiple
            sx={{ width: '100%' }}
            options={internalUserOptions}
            getOptionLabel={(option) => option.fullName}
            value={selectedInternalUsers}
            onChange={handleChangeInternalUsers}
            disableCloseOnSelect
            filterSelectedOptions
            loading={isLoadingInternalUsers}
            renderTags={() => null}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.fullName}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('actionPlan.users')}
                placeholder={t('actionPlan.search')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingInternalUsers ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
        {selectedInternalUsers.length > 0 && (
          <Box>
            <ChipsList
              value={internalUsersAsItems}
              colorsByChips={internalUserColors}
              handleDelete={handleRemoveInternalUser}
            />
          </Box>
        )}
      </Box>

      <Box sx={styles.notificationsSection}>
        <Typography sx={styles.notificationsSectionTitle}>
          {t('general.externalUsers')}
        </Typography>
        <Typography sx={styles.notificationsSectionHelper}>
          {t('actionPlan.enterExternalEmails')}
        </Typography>
        <Box sx={{ marginTop: '12px', }}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            value={emailsRawInput}
            onChange={handleEmailsChange}
            placeholder={t('actionPlan.emailPlaceholder')}
            error={!!emailError}
            helperText={emailError}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default Step4Notifications
